import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Row,
  Col,
  Pagination,
  Collapse,
  Table,
  Tag,
  Button,
  Drawer,
  Modal,
  Input,
  Select,
  DatePicker,
  Space,
  message,
} from "antd";
import { CaretRightOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import PageHeader from "../Layout/PageHeader";
import { formatDateTime } from "../../helpers/date";
import { PERMISSIONS } from "../../constants/roles";
import { usePermissions } from "../App/PermissionContext";

const { RangePicker } = DatePicker;

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

const STATUS_COLORS = {
  PENDING: "blue",
  FULFILLED: "green",
  FAILED: "red",
  CANCELLED: "orange",
  CANCELLING: "orange",  // transient — revert to FULFILLED or completes to CANCELLED
};

const Redemption = () => {
  const { permissions } = usePermissions();
  const [pagination, setPagination] = useState({ currentPage: 1, limit: 20 });
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  // Filter params shared by the list fetch and the CSV export, so "what you see" == "what you export".
  const buildFilterParams = useCallback(
    () => ({
      ...(filters.userEmail && { userEmail: filters.userEmail }),
      ...(filters.status && { status: filters.status }),
      ...(filters.startDate && { startDate: filters.startDate.toISOString() }),
      // Clone before calling endOf("day") — moment mutates in place, which would
      // permanently alter the moment object held in filters.endDate state.
      ...(filters.endDate && { endDate: filters.endDate.clone().endOf("day").toISOString() }),
    }),
    [filters],
  );

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        ...buildFilterParams(),
        page: pagination.currentPage,
        limit: pagination.limit,
      };
      const res = await axios.get("/api/redemptions", { params });
      setItems(res.data.data);
      setPagination((prev) => ({ ...prev, totalSize: res.data.pagination?.totalSize }));
    } catch (error) {
      message.error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, buildFilterParams]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handlePageChange = (currentPage, limit) => {
    setPagination((prev) => ({ ...prev, currentPage, limit }));
  };

  // Reset pagination to page 1 whenever filters change so stale pages don't show empty tables
  const handleFilterChange = (updater) => {
    setFilters(updater);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  // Debounce user-email text input — avoids firing a request on every keystroke
  const userEmailDebounce = useRef(null);

  // Clear debounce timer on unmount to prevent state updates on unmounted component
  useEffect(() => () => clearTimeout(userEmailDebounce.current), []);
  const handleUserEmailChange = (e) => {
    const value = e.target.value.trim() || undefined;
    clearTimeout(userEmailDebounce.current);
    userEmailDebounce.current = setTimeout(() => {
      handleFilterChange((f) => ({ ...f, userEmail: value }));
    }, 400);
  };

  // Async CSV export of the full redemption detail, honouring the current filters. The backend
  // queues the job and emails a download link (house export pattern) — mirrors DiscountCode export.
  const handleExport = () => {
    Modal.confirm({
      title: "Download Redemption History?",
      content:
        "Export the redemption history as CSV with the current filters applied. The download link will appear under System -> Jobs once processing completes.",
      async onOk() {
        try {
          await axios.get("/api/redemptions/export", { params: buildFilterParams() });
          message.info("Please check the Export progress in System -> Jobs");
        } catch (error) {
          message.error(error.response?.data?.message || error.message);
        }
      },
    });
  };

  const openCancel = () => {
    setCancelReason("");
    setCancelOpen(true);
  };

  const submitCancel = async ({ force = false } = {}) => {
    if (!selected) return;
    if (!cancelReason.trim()) {
      message.warning("Reason is required");
      return;
    }
    setCancelling(true);
    try {
      await axios.post(`/api/redemptions/${selected.objectId}/cancel`, {
        reason: cancelReason.trim(),
        ...(force && { force: true, acknowledgeDoubleSpend: true }),
      });
      message.success("Redemption cancelled and points refunded");
      setCancelOpen(false);
      setSelected(null);
      fetchItems();
    } catch (error) {
      // 409 = voucher already used on an order. On the first (non-forced) attempt,
      // close the primary modal first so dialogs don't stack, then show the confirm.
      // The !force guard prevents re-prompting if the forced call itself fails.
      if (!force && error.response?.status === 409) {
        setCancelOpen(false);
        const errMsg = error.response?.data?.message || "Voucher was already used on an order.";
        Modal.confirm({
          title: "Voucher already redeemed on an order",
          content: `${errMsg}\n\nProceeding will refund the user's points even though the voucher discount was already applied to an order. This results in a double-credit. Are you sure?`,
          okText: "Force cancel (double-credit)",
          okButtonProps: { danger: true },
          cancelText: "Abort",
          onOk: () => submitCancel({ force: true, acknowledgeDoubleSpend: true }),
        });
      } else {
        message.error(error.response?.data?.message || error.message);
      }
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    {
      title: "User",
      key: "user",
      width: 220,
      render: (_, r) => r.user?.email || r.user?.objectId || "-",
    },
    {
      title: "Item",
      key: "item",
      width: 220,
      render: (_, r) => r.rewardItemSnapshot?.name || r.rewardItem?.name || "-",
    },
    {
      title: "Points",
      dataIndex: "pointsSpent",
      key: "pointsSpent",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Month",
      dataIndex: "monthKey",
      key: "monthKey",
      width: 100,
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (t) => (t ? formatDateTime(t) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_, record) => (
        <Button type="link" onClick={() => setSelected(record)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Redemptions"
        childRoutes={[{ path: "/redemptions", breadcrumbName: "Redemptions" }]}
      />

      {permissions?.includes(PERMISSIONS.EXPORT_REDEMPTION_LIST) && (
        <Row justify="end" style={{ marginBottom: 16 }}>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Export CSV
          </Button>
        </Row>
      )}

      <Collapse
        expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
      >
        <Collapse.Panel header="Filters" key="filters">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>User Email</span>
                <Input
                  allowClear
                  placeholder="Search by user email"
                  onChange={handleUserEmailChange}
                />
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Status</span>
                <Select
                  allowClear
                  placeholder="ALL"
                  style={{ width: "100%" }}
                  onChange={(v) => handleFilterChange((f) => ({ ...f, status: v }))}
                >
                  <Select.Option value="PENDING">Pending</Select.Option>
                  <Select.Option value="FULFILLED">Fulfilled</Select.Option>
                  <Select.Option value="FAILED">Failed</Select.Option>
                  <Select.Option value="CANCELLED">Cancelled</Select.Option>
                  <Select.Option value="CANCELLING">Cancelling</Select.Option>
                </Select>
              </Space>
            </Col>
            <Col xs={24} sm={8}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <span>Date range</span>
                <RangePicker
                  style={{ width: "100%" }}
                  onChange={(v) =>
                    handleFilterChange((f) => ({
                      ...f,
                      startDate: v?.[0] || undefined,
                      endDate: v?.[1] || undefined,
                    }))
                  }
                />
              </Space>
            </Col>
          </Row>
        </Collapse.Panel>
      </Collapse>

      <Table
        rowKey="objectId"
        columns={columns}
        dataSource={items}
        pagination={false}
        loading={loading}
        scroll={{ x: true }}
        locale={{ emptyText: "Not available" }}
      />

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            total={pagination?.totalSize}
            pageSize={pagination.limit}
            current={pagination.currentPage}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onChange={handlePageChange}
          />
        </Col>
      </Row>

      <Drawer
        title="Redemption detail"
        visible={!!selected}
        onClose={() => setSelected(null)}
        width={520}
      >
        {selected && (
          <div>
            <p><strong>ID:</strong> {selected.objectId}</p>
            <p><strong>Status:</strong> <Tag color={STATUS_COLORS[selected.status]}>{selected.status}</Tag></p>
            <p><strong>User:</strong> {selected.user?.email || selected.user?.objectId}</p>
            <p><strong>Item:</strong> {selected.rewardItemSnapshot?.name}</p>
            <p><strong>Type:</strong> {selected.rewardItemSnapshot?.type}</p>
            <p><strong>Points spent:</strong> {selected.pointsSpent}</p>
            <p><strong>Month:</strong> {selected.monthKey}</p>
            <p><strong>Created:</strong> {formatDateTime(selected.createdAt)}</p>
            {selected.fulfilledAt && <p><strong>Fulfilled:</strong> {formatDateTime(selected.fulfilledAt)}</p>}
            {selected.cancelledAt && <p><strong>Cancelled:</strong> {formatDateTime(selected.cancelledAt)}</p>}
            {selected.cancellationReason && (
              <p><strong>Cancellation reason:</strong> {selected.cancellationReason}</p>
            )}
            {selected.fulfillmentResult?.voucherId && (
              <p><strong>Voucher ID:</strong> {selected.fulfillmentResult.voucherId}</p>
            )}
            {selected.failureReason && (
              <p><strong>Failure reason:</strong> {selected.failureReason}</p>
            )}

            {permissions?.includes(PERMISSIONS.CANCEL_REDEMPTION) &&
              selected.status === "FULFILLED" && (
                <Button type="primary" danger onClick={openCancel} style={{ marginTop: 16 }}>
                  Cancel & refund points
                </Button>
              )}
          </div>
        )}
      </Drawer>

      <Modal
        title="Cancel redemption"
        visible={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        onOk={() => submitCancel()}
        confirmLoading={cancelling}
        okText="Confirm cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Refunds {selected?.pointsSpent} points to the user via a compensating EARN
          transaction, revokes the issued voucher (if VOUCHER type), and writes an audit log entry.
        </p>
        <Input.TextArea
          rows={3}
          placeholder="Reason (required)"
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Redemption;
